import * as THREE from 'three';
import React from 'react';
import Engine from './Engine';
import Scenario from './scenarioElements/Scenario';
import AssetUtility from './AssetUtility';
import ScopeCamera from './Camera';
import ScopeLight from './Light';
import { GLTFExporter } from './three-extensions/jsm/exporters/GLTFExporter';
import Callout from './Callout';
import Text3D from './Text3D';
import MediaPanel from './MediaPanel';
import MathUtils from './utilities/MathUtils';
import SceneObjectUtils from './utilities/SceneObjectUtils';
import TransformUtils from './utilities/TransformUtils';
import ScenarioHealthUtils from './utilities/ScenarioHealthUtils';
import {CustomMaterials} from "./CustomMaterials";
import { assert } from './utilities/ErrorHandling';
import SceneCameraController from './SceneCameraController';
import Utils from './Utilities';
import MaterialAsset from './MaterialAsset';
import AssetsManager from './AssetsManager';
import { getUuidv4 } from './utilities/Misc';
import { RenderLayers, TrackerTypes, CustomEventTypes, RenderModes, PrimitiveTypes, defaultPivot, SimulationStates } from '../globals';
import MessagePrompter from '../components/prompts/MessagePrompter';
import { ITrackerSaveData } from '../interfaces/ProjectSave';
import StandbyOverlay from '../components/layout/StandbyOverlay';
import store from '../state/store';

class ARTracker extends THREE.Mesh
{
	static defaultImageTexture : THREE.Texture;
	static trackersRoot: THREE.Object3D;
	static glbBlobs = new Map<string, Blob>();
	trackerType: TrackerTypes = TrackerTypes.IMAGE;
	modelInstance?: THREE.Mesh;
	modelPreviewInstance?: THREE.Mesh;
	posePreviewScene: THREE.Scene;
	modelTrackerCameraController: SceneCameraController;
	isDefaultTracker: boolean;
	modelAssetId: string;
	sceneSelectionModels: THREE.Object3D[];
	modelTrackerCamera: THREE.PerspectiveCamera;
	modelTracker?: THREE.Object3D;
	slamTracker?: THREE.Mesh;
	imageTracker?: THREE.Mesh;
	imageTexture?: THREE.Texture;
	imageAssetId?: string;
	autoInit: boolean;
	laplaceThreshold: number;
	normalThreshold: number;
	minInlierRatioTracking: number;
	minInlierRatioInit: number;
	lineGradientThreshold: number;
	lineSearchLengthInitRelative: number;
	lineSearchLengthTrackingRelative: number;
	keyFrameDistance: number;
	fovPercent: number;
	displayMode: RenderModes;
	imageAspectRatio: number = 1;
	imageSize: number;
	sequence: any;
	guideImageAssetId: string;
	guideTitle: string;
	guideTitleVisible: boolean;
	guideCaption: string;
	guideCaptionVisible: boolean;
	guideSize: number;
	contentCanBeScaled: boolean;
	contentCanBeRotated: boolean;

	static loadDefaultTrackerImage()
	{
		return new Promise<THREE.Texture>((resolve) =>
		{
			if(!ARTracker.defaultImageTexture)
			{
				const texloader = new THREE.TextureLoader();
				texloader.load('../defaultImageTracker.jpg', (texture) => 
				{
					texture.flipY = false;
					texture.colorSpace = THREE.SRGBColorSpace;
					ARTracker.defaultImageTexture = texture;
					document.dispatchEvent(new CustomEvent(CustomEventTypes.onImageTargetTextureLoaded));
					resolve(texture);
				});
			}
		});
	}

	static init()
	{
		ARTracker.trackersRoot = new THREE.Object3D();
		ARTracker.trackersRoot.userData.hideInHierarchy = true;
		ARTracker.trackersRoot.userData.ignoreExport = true;
	}

	static clearData()
	{
		for (let i = ARTracker.getTrackerList().length - 1; i >= 0; i--)
		{
			const tracker = ARTracker.getTrackerList()[i];
			tracker.clearModelTrackingObjects();
			SceneObjectUtils.disposeObject(tracker, false);
		}
		ARTracker.glbBlobs.clear();
	}

	static load(data: ITrackerSaveData[])
	{
		if (!data) return;
		for (const trackerData of data)
		{
			const tracker = new ARTracker(trackerData.type, trackerData.id, true);
			tracker.name = trackerData.name;

			if (trackerData.displayMode)
			{
				tracker.setDisplayMode(trackerData.displayMode);
			}

			switch (tracker.trackerType)
			{
				case TrackerTypes.IMAGE:
					{
						tracker.setImage(trackerData.image || "");
						tracker.setImageSize(trackerData.size || 0.1);
					}
					break;
				case TrackerTypes.MODEL:
					{
						tracker.setPoseAutoInit(trackerData.autoInit || false);
						
						if (trackerData.sceneSelectionModels)
						{
							ARTracker.loadSceneSelection(trackerData, tracker);
						}
						else
						{
							tracker.setModel(trackerData.model || "");
						}
						
						if(trackerData.advancedOptions)
						{
							//backwards comptability fix
							if (trackerData.advancedOptions.minInlierRatioTracking > trackerData.advancedOptions.minInlierRatioInit)
							{
								const temp = trackerData.advancedOptions.minInlierRatioTracking;
								trackerData.advancedOptions.minInlierRatioTracking = trackerData.advancedOptions.minInlierRatioInit;
								trackerData.advancedOptions.minInlierRatioInit = temp;
							}

							tracker.setLaplaceThreshold(trackerData.advancedOptions.laplaceThreshold);
							tracker.setNormalThreshold(trackerData.advancedOptions.normalThreshold);
							tracker.setMinInlierRatioInit(trackerData.advancedOptions.minInlierRatioInit);
							tracker.setMinInlierRatioTracking(trackerData.advancedOptions.minInlierRatioTracking);
							tracker.setLineGradientThreshold(trackerData.advancedOptions.lineGradientThreshold);
							tracker.setLineSearchLengthInitRelative(trackerData.advancedOptions.lineSearchLengthInitRelative);
							tracker.setLineSearchLengthTrackingRelative(trackerData.advancedOptions.lineSearchLengthTrackingRelative);
							tracker.setKeyFrameDistance(trackerData.advancedOptions.keyFrameDistance);
							tracker.setKeyFrameDistance(trackerData.advancedOptions.fovPercent);
						}

						let anchorDistance = trackerData.cameraAnchorDistance;
						if (!anchorDistance)
						{
						//old format without previewModel information and a camera that's in a space local to the tracker object, must convert to new format:
							tracker.attach(tracker.modelTrackerCamera);
							trackerData.cameraPosition && tracker.modelTrackerCamera.position.set(trackerData.cameraPosition.x, trackerData.cameraPosition.y, trackerData.cameraPosition.z);
							trackerData.cameraRotation && tracker.modelTrackerCamera.rotation.set(trackerData.cameraRotation.x, trackerData.cameraRotation.y, trackerData.cameraRotation.z);

							if (tracker.modelInstance)
							{
								tracker.modelInstance.attach(tracker.modelTrackerCamera);
								const tempPos = new THREE.Vector3().copy(tracker.modelTrackerCamera.position);
								const tempQuat = new THREE.Quaternion().copy(tracker.modelTrackerCamera.quaternion);
								const previewInstance = tracker.modelPreviewInstance!.children[0];
								previewInstance.attach(tracker.modelTrackerCamera);
								tracker.modelTrackerCamera.position.copy(tempPos);
								tracker.modelTrackerCamera.quaternion.copy(tempQuat);
							}
							tracker.posePreviewScene.attach(tracker.modelTrackerCamera);
							anchorDistance = 0.5;
						}
						else
						{
							if (trackerData.modelPreviewPosition && tracker.modelPreviewInstance)
							{
								tracker.modelPreviewInstance.position.set(trackerData.modelPreviewPosition.x, trackerData.modelPreviewPosition.y, trackerData.modelPreviewPosition.z);
								trackerData.modelPreviewRotation && tracker.modelPreviewInstance.rotation.set(trackerData.modelPreviewRotation.x, trackerData.modelPreviewRotation.y, trackerData.modelPreviewRotation.z);
							}

							trackerData.cameraPosition && tracker.modelTrackerCamera.position.set(trackerData.cameraPosition.x, trackerData.cameraPosition.y, trackerData.cameraPosition.z);
							trackerData.cameraRotation && tracker.modelTrackerCamera.rotation.set(trackerData.cameraRotation.x, trackerData.cameraRotation.y, trackerData.cameraRotation.z);
						}

						const cameraForward = new THREE.Vector3();
						const cameraPosition = new THREE.Vector3();
						tracker.modelTrackerCamera.getWorldDirection(cameraForward);
						tracker.modelTrackerCamera.getWorldPosition(cameraPosition);
						tracker.modelTrackerCameraController.getAnchor().addVectors(cameraPosition, cameraForward.multiplyScalar(anchorDistance));
						tracker.modelTrackerCamera.userData.anchorDistance = anchorDistance;
					}
					break;
				case TrackerTypes.SLAM:
					{
						tracker.isDefaultTracker = trackerData.isDefaultTracker || false;
					}
					break;
			}

			if (Array.isArray(trackerData.translation))
			{
				tracker.position.fromArray(trackerData.translation);
			}
			else
			{
				tracker.position.x = trackerData.translation.x;
				tracker.position.y = trackerData.translation.y;
				tracker.position.z = trackerData.translation.z;
			}

			if (Array.isArray(trackerData.rotation))
			{
				tracker.quaternion.fromArray([trackerData.rotation.x, trackerData.rotation.y, trackerData.rotation.z, trackerData.rotation.w]);
			}
			else
			{
				tracker.quaternion.x = trackerData.rotation.x;
				tracker.quaternion.y = trackerData.rotation.y;
				tracker.quaternion.z = trackerData.rotation.z;
				tracker.quaternion.w = trackerData.rotation.w;
			}

			tracker.setGuideImageAssetId(trackerData.guideImageAssetId);
			tracker.setGuideTitle(trackerData.guideTitle);
			tracker.setGuideTitleVisible(Boolean(trackerData.guideTitleVisible));
			tracker.setGuideCaption(trackerData.guideCaption);
			tracker.setGuideCaptionVisible(Boolean(trackerData.guideCaptionVisible));
			tracker.setGuideSize(trackerData.guideSize);
			tracker.setContentCanBeScaled(trackerData.contentCanBeScaled ?? true);
			tracker.setContentCanBeRotated(trackerData.contentCanBeRotated ?? true);

			ARTracker.trackersRoot.add(tracker);
		}
		document.dispatchEvent(new CustomEvent(CustomEventTypes.onTrackerListUpdated));
	}

	static save(referencedAssetsList: Set<string>, isExport = false, modelAssetsList? : string[], textureList? : string[]) : ITrackerSaveData[]
	{
		const output: ITrackerSaveData[] = [];

		for (const tracker of ARTracker.getTrackerList())
		{
			const trackerData : ITrackerSaveData = {
				name: tracker.name,
				id: tracker.userData.guid,
				type: tracker.trackerType,
				displayMode: tracker.displayMode,
				translation: { x: tracker.position.x, y: tracker.position.y, z: tracker.position.z },
				rotation: { x: tracker.quaternion.x, y: tracker.quaternion.y, z: tracker.quaternion.z, w: tracker.quaternion.w },
				guideImageAssetId: tracker.guideImageAssetId,
				guideTitle: tracker.guideTitle,
				guideTitleVisible: tracker.guideTitleVisible,
				guideCaption: tracker.guideCaption,
				guideCaptionVisible: tracker.guideCaptionVisible,
				guideSize: tracker.guideSize,
			};

			// when exporting to the app, we want to remove the guide title and caption if they are not visible even if they're defined
			if(isExport)
			{
				trackerData.guideTitle = trackerData.guideTitleVisible ? trackerData.guideTitle : "";
				trackerData.guideCaption = trackerData.guideCaptionVisible ? trackerData.guideCaption : "";

				// these fields are irrelevant for the app so we might as well delete them and save some data in the exported file
				delete trackerData.guideTitleVisible;
				delete trackerData.guideCaptionVisible;
			}

			referencedAssetsList.add(tracker.guideImageAssetId);

			switch (tracker.trackerType)
			{
				case TrackerTypes.IMAGE:
					{
						trackerData.image = tracker.imageAssetId;
						trackerData.size = tracker.imageSize;
						if (trackerData.image && !referencedAssetsList.has(trackerData.image))
						{
							referencedAssetsList.add(trackerData.image);
							if (textureList) textureList.push(trackerData.image);
						}
					}
					break;
				case TrackerTypes.MODEL:
					{
						const modelInfo = AssetsManager.getAssetInfo(tracker.modelAssetId);
						trackerData.model = tracker.modelAssetId;
						trackerData.autoInit = tracker.autoInit;
						trackerData.advancedOptions = {
							laplaceThreshold: tracker.laplaceThreshold,
							normalThreshold: tracker.normalThreshold,
							minInlierRatioInit: tracker.minInlierRatioInit,
							minInlierRatioTracking: tracker.minInlierRatioTracking,
							lineGradientThreshold: tracker.lineGradientThreshold,
							lineSearchLengthInitRelative: tracker.lineSearchLengthInitRelative,
							lineSearchLengthTrackingRelative: tracker.lineSearchLengthTrackingRelative,
							keyFrameDistance: tracker.keyFrameDistance,
							fovPercent: tracker.fovPercent / 100,
						};

						if (isExport)
						{
							if (!tracker.autoInit)
							{
								trackerData.initPoseCamera = tracker.modelTrackerCamera.userData.guid;
							}
							if (tracker.modelAssetId && !referencedAssetsList.has(tracker.modelAssetId))
							{
								const extension = modelInfo ? modelInfo.extension ?? Utils.getFileExtension(modelInfo.name) : 'glb';
								if (extension === 'obj')
								{
									referencedAssetsList.add(tracker.modelAssetId);
								}
							}
						}
						else
						{
							trackerData.cameraAnchorDistance = tracker.modelTrackerCamera.userData.anchorDistance;
							trackerData.cameraPosition = { x: tracker.modelTrackerCamera.position.x, y: tracker.modelTrackerCamera.position.y, z: tracker.modelTrackerCamera.position.z };
							trackerData.cameraRotation = { x: tracker.modelTrackerCamera.rotation.x, y: tracker.modelTrackerCamera.rotation.y, z: tracker.modelTrackerCamera.rotation.z };

							if (tracker.modelPreviewInstance)
							{
								trackerData.modelPreviewPosition = { x: tracker.modelPreviewInstance.position.x, y: tracker.modelPreviewInstance.position.y, z: tracker.modelPreviewInstance.position.z };
								trackerData.modelPreviewRotation = { x: tracker.modelPreviewInstance.rotation.x, y: tracker.modelPreviewInstance.rotation.y, z: tracker.modelPreviewInstance.rotation.z };
							}

							if (modelInfo)
							{
								if (tracker.modelAssetId && !referencedAssetsList.has(tracker.modelAssetId))
								{
									referencedAssetsList.add(tracker.modelAssetId);
									if (modelAssetsList) modelAssetsList.push(tracker.modelAssetId);
								}
							}
							else if (tracker.modelAssetId)
							{
								ARTracker.saveSceneSelection(trackerData, tracker, referencedAssetsList, modelAssetsList);
							}
						}
					}
					break;
				case TrackerTypes.SLAM:
					{
						trackerData.isDefaultTracker = tracker.isDefaultTracker;
						trackerData.contentCanBeScaled = tracker.contentCanBeScaled;
						trackerData.contentCanBeRotated = tracker.contentCanBeRotated;
					}
					break;
			}

			output.push(trackerData);
		}
		return output;
	}

	static saveSceneSelection(trackerData: ITrackerSaveData, tracker: ARTracker, referencedAssetsList: Set<string>, modelAssetsList?: string[])
	{
		trackerData.sceneSelectionModels = [];
		for (const selectedModel of tracker.modelInstance!.children)
		{
			const guidToNode = new Map();
			selectedModel.traverse(function(node)
			{
				const nodeData =
					{
						position: node.position.toArray(),
						quaternion: node.quaternion.toArray(),
						scale: node.scale.toArray(),
						name: node.name,
						...((node.userData.pivot && !MathUtils.areVectorsEqual(node.userData.pivot.relativeOffset, { x: 0, y: 0, z: 0 })) && { pivot:
							{
								relativeOffset: node.userData.pivot.relativeOffset,
								geometryOffset: node.userData.pivot.geometryOffset,
								space: node.userData.pivot.space,
							} }),
						children: [],
					} as any; // to allow additional properties

				guidToNode.set(node.userData.guid, nodeData);
				if (node.userData.assetId)
				{
					nodeData.assetId = node.userData.assetId;
					nodeData.nodeIndex = node.userData.modelData.nodeIndex;
					nodeData.primitiveIndex = node.userData.modelData.primitiveIndex;
					if (!referencedAssetsList.has(node.userData.assetId))
					{
						referencedAssetsList.add(node.userData.assetId);
						if (modelAssetsList) modelAssetsList.push(node.userData.assetId);
					}
				}
				else if (node.userData.primitiveType)
				{
					nodeData.primitiveType = node.userData.primitiveType;
					nodeData.primitiveScale = node.userData.primitiveScale;
				}

				const parentNodeData = guidToNode.get(node.parent!.userData.guid);
				if (!parentNodeData)
				{
					trackerData.sceneSelectionModels!.push(nodeData);
				}
				else
				{
					parentNodeData.children.push(nodeData);
				}
			});
		}
	}

	static loadSceneSelection(trackerData: ITrackerSaveData, tracker: ARTracker)
	{
		const objects: THREE.Object3D[] = [];
		const skinnedObjects: THREE.SkinnedMesh[] = [];

		const generateObject = (nodeData) =>
		{
			let object: THREE.Object3D;
			if (nodeData.assetId)
			{
				const assetRoot = Engine.getInstance().getModelAsset(nodeData.assetId)!;
				const srcObject = SceneObjectUtils.getSubNode(assetRoot, nodeData.nodeIndex, nodeData.primitiveIndex, nodeData.assetId)!;
				object = SceneObjectUtils.cloneNoneRecursive(srcObject);
			}
			else if (nodeData.primitiveType)
			{
				const geometry = Utils.getPrimitiveGeometry(nodeData.primitiveType, nodeData.primitiveScale);
				object = new THREE.Mesh(geometry);
				object.userData.primitiveType = nodeData.primitiveType;
				object.userData.primitiveScale = nodeData.primitiveScale;
			}
			else
			{
				object = new THREE.Object3D();
			}
			object.name = nodeData.name;
			object.position.fromArray(nodeData.position);
			object.quaternion.fromArray(nodeData.quaternion);
			object.scale.fromArray(nodeData.scale);
			SceneObjectUtils.initialiseObjectProperties(object);

			if (nodeData.pivot)	TransformUtils.loadPivot(object, nodeData.pivot.relativeOffset, nodeData.pivot.geometryOffset, nodeData.pivot.space);

			if (nodeData.children)
			{
				for (const child of nodeData.children)
				{
					object.add(generateObject(child));
				}
			}

			if(object instanceof THREE.SkinnedMesh)
			{
				skinnedObjects.push(object);
			}
			return object;
		}

		for (const sceneSelection of trackerData.sceneSelectionModels!)
		{
			objects.push(generateObject(sceneSelection));
		}

		for(const skinnedObject of skinnedObjects)
		{
			SceneObjectUtils.reconstructSkeleton(skinnedObject, true);
		}

		tracker.setModelFromSceneSelection(objects);
	}

	static getTrackerRoot()
	{
		return ARTracker.trackersRoot;
	}

	static onSceneChange()
	{
		const selectionRoot = Engine.getInstance().getSelectionRoot();
		if (selectionRoot)
		{
			selectionRoot.add(ARTracker.trackersRoot);
			selectionRoot.children.splice(0, 0, selectionRoot.children.splice(selectionRoot.children.length-1, 1)[0]);
		}

		const visibleTrackerIds = Scenario.currentStep()?.sequence.trackersStep.trackers;
		if(visibleTrackerIds)
		{
			for (const tracker of ARTracker.getTrackerList())
			{
				tracker.setVisible(visibleTrackerIds.includes(tracker.userData.guid));
			}
		}

		// WC20-5472: hide SLAM trackers if we're simulating as viewers
		const isSimulationPlaying = store.getState().applicationModule.simulationState === SimulationStates.Playing;
		const isSimulatingAsViewer = store.getState().applicationModule.simulationAsViewer;
		if (isSimulationPlaying && isSimulatingAsViewer)
		{
			const arTrackers = ARTracker.getTrackerList();
			for (const tracker of arTrackers)
			{
				tracker.setVisible(false);
			}
		}
	}

	static getTrackerList() : ARTracker[]
	{
		return ARTracker.trackersRoot.children as ARTracker[];
	}

	static findTrackerById(trackerId)
	{
		for(const tracker of ARTracker.getTrackerList())
		{
			if (tracker.userData.guid === trackerId) return tracker;
		}

		return null;
	}

	static setTrackersVisibility(visible)
	{
		ARTracker.trackersRoot.visible = visible;
	}
	static getTrackersVisibility()
	{
		return ARTracker.trackersRoot.visible;
	}

	static createGlbBlob(tracker: ARTracker)
	{
		const assetId = tracker.modelAssetId;
		if (ARTracker.glbBlobs.has(assetId)) {return ARTracker.glbBlobs.get(assetId);}

		let exportInstance : THREE.Object3D;
		if (tracker.sceneSelectionModels.length)
		{
			exportInstance = SceneObjectUtils.cloneObject(tracker.modelInstance!);
		}
		else
		{
			tracker.posePreviewScene.attach(tracker.modelInstance!);
			exportInstance = SceneObjectUtils.cloneObject(tracker.modelInstance!);
			tracker.modelTracker!.attach(tracker.modelInstance!);
		}

		const modelRoots: THREE.Object3D[] = [];
		exportInstance.traverse(function(child)
		{
			if (child instanceof THREE.Mesh)
			{
				if (child.material.userData.type === "modelRoot")
				{
					modelRoots.push(child);
				}	
				else 
				{
					child.material = MaterialAsset.defaultMaterial.internalMaterial;
				}												
			}
			child.userData = {};
		});

		for (const root of modelRoots)
		{
			const dummyRoot = new THREE.Object3D();
			if (root == exportInstance)
			{
				exportInstance = dummyRoot;
			}
			if (root.parent)
			{
				root.parent.attach(dummyRoot);
				root.removeFromParent();
			}
			dummyRoot.position.copy(root.position);
			dummyRoot.quaternion.copy(root.quaternion);
			dummyRoot.scale.copy(root.scale);
			const numChildren = root.children.length;
			for (let i = numChildren - 1; i >= 0; i--)
			{
				const child = root.children[i];
				dummyRoot.attach(child);
			}
			SceneObjectUtils.disposeObject(root, false);
		}

		const exporter = new GLTFExporter();
		StandbyOverlay.startStandbyMode();
		exporter.parse(exportInstance, function(result)
		{
			const blob = new Blob([result], { type: 'application/octet-stream' });
			ARTracker.glbBlobs.set(assetId, blob);
			SceneObjectUtils.disposeObject(exportInstance, false);

			const i = Math.floor(Math.log(blob.size) / Math.log(1024)); // [Bytes, KB, MB, GB, ....];
			const size = blob.size / Math.pow(1024, i);

			if (i > 2 || (i == 2 && size > 40))
			{
				MessagePrompter.showWarning(
					{
						title: "Size warning",
						content:
						<div>
							Model is larger than 40MB and will not work on Hololens 2<br />
						</div>,
						actions: [
							{
								label: "Confirm",
								onClick: () =>
								{
									StandbyOverlay.stopStandbyMode();
									return ARTracker.glbBlobs.get(assetId);
								},
							},
						]
					});
			}
			else
			{
				StandbyOverlay.stopStandbyMode();
				return ARTracker.glbBlobs.get(assetId);
			}
		}, 
		(error) => 
		{
			console.error(error);
			StandbyOverlay.stopStandbyMode();
		}, 
		{ binary: true });
	}

	constructor(type: TrackerTypes = TrackerTypes.IMAGE, guid?: string, supressEvent = false)
	{
		super(new THREE.BoxGeometry(1, 1, 1), CustomMaterials.getModelRootMaterial());
		this.onDefaultTargetImageloaded = this.onDefaultTargetImageloaded.bind(this);
		this.constructGeometries();
		this.name = "AR Tracker";
		this.userData.guid = guid ? guid : getUuidv4();

		this.attach(this.slamTracker!);
		this.attach(this.imageTracker!);
		this.attach(this.modelTracker!);

		this.modelAssetId = "";
		this.sceneSelectionModels = [];
		this.autoInit = false;
		this.laplaceThreshold = 50.0;
		this.normalThreshold = 3.0;
		this.minInlierRatioTracking = 0.5;
		this.minInlierRatioInit = 0.6;
		this.lineGradientThreshold = 40;
		this.lineSearchLengthInitRelative = 0.0413;
		this.lineSearchLengthTrackingRelative = 0.0313;
		this.keyFrameDistance = 50;
		this.fovPercent = 0.6;

		this.isDefaultTracker = false;
		this.displayMode = RenderModes.Standalone;
		this.imageSize = 0.1;
		this.setImageSize(0.1);
		this.setTrackerType(type);

		this.posePreviewScene = new THREE.Scene();
		this.modelTrackerCamera = new THREE.PerspectiveCamera(60, 16 / 9, 0.001, 10000);
		this.posePreviewScene.attach(this.modelTrackerCamera);
		this.modelTrackerCamera.layers.enable(RenderLayers.ModelTrackerPreview);
		this.modelTrackerCamera.userData.renderIsDirty = true;
		this.modelTrackerCamera.userData.guid = getUuidv4();
		this.modelTrackerCameraController = new SceneCameraController(this.modelTrackerCamera);

		this.guideImageAssetId = "";
		this.guideTitle = "Image Guide";
		this.guideTitleVisible = true;
		this.guideCaption = "Tap to Zoom";
		this.guideCaptionVisible = true;
		this.guideSize = 1;
		this.contentCanBeScaled = true;
		this.contentCanBeRotated = true;

		document.addEventListener(CustomEventTypes.onImageTargetTextureLoaded, this.onDefaultTargetImageloaded);

		this.userData.requiresOutline = true;


		this.traverse(function(child)
		{
			child.userData.hideInHierarchy = true;
			child.layers.set(RenderLayers.MainCameraOnly);
		});

		if (!supressEvent)
		{document.dispatchEvent(new CustomEvent(CustomEventTypes.onTrackerListUpdated));}
	}

	constructGeometries = () =>
	{
		// image tracker
		this.imageTracker = new THREE.Mesh( Utils.getPrimitiveGeometry(PrimitiveTypes.Plane), new THREE.MeshBasicMaterial());
		this.imageTracker.userData.requiresOutline = true;
		this.imageTracker.geometry.computeBoundingBox();
		if (ARTracker.defaultImageTexture)
		{
			this.setImage(ARTracker.defaultImageTexture);
		}

		// slam tracker
		this.slamTracker = new THREE.Mesh(new THREE.BoxGeometry(0.05, 0.05, 0.05), CustomMaterials.getModelRootMaterial());
		const materialX = new THREE.LineBasicMaterial({ color: 0xff0000 });
		const geometryX = new THREE.BufferGeometry();
		const verticesX = new Float32Array( [
			0.05, 0, 0,
			-0.05, 0, 0,
		] );
		geometryX.setAttribute( 'position', new THREE.BufferAttribute( verticesX, 3 ) );
		const lineX = new THREE.Line(geometryX, materialX);
		this.slamTracker.add(lineX);

		const materialY = new THREE.LineBasicMaterial({ color: 0x00ff00 });
		const geometryY = new THREE.BufferGeometry();
		const verticesY = new Float32Array( [
			0, 0.05, 0,
			0, -0.05, 0,
		] );
		geometryY.setAttribute( 'position', new THREE.BufferAttribute( verticesY, 3 ) );

		const lineY = new THREE.Line(geometryY, materialY);
		this.slamTracker.add(lineY);

		const materialZ = new THREE.LineBasicMaterial({ color: 0x0000ff });
		const geometryZ = new THREE.BufferGeometry();
		const verticesZ = new Float32Array( [
			0, 0, 0.05,
			0, 0, -0.05,
		] );
		geometryZ.setAttribute( 'position', new THREE.BufferAttribute( verticesZ, 3 ) );
		const lineZ = new THREE.Line(geometryZ, materialZ);
		this.slamTracker.add(lineZ);

		const forwardIndicator = new THREE.Mesh(new THREE.CylinderGeometry(0, 0.05, 0.2, 12, 1, false), materialZ);
		forwardIndicator.scale.set(0.05, 0.05, 0.05);
		forwardIndicator.translateZ(0.05);
		forwardIndicator.rotateX(90 / 180 * Math.PI);

		this.slamTracker.add(forwardIndicator);

		const curve = new THREE.EllipseCurve(0, 0, 0.025, 0.025, 0, 2 * Math.PI);
		const points = curve.getPoints( 32 );
		const circleGeometry = new THREE.BufferGeometry().setFromPoints( points );
		const circleMaterial = new THREE.LineBasicMaterial( { color: 0xffffff } );
		const circle = new THREE.Line( circleGeometry, circleMaterial );
		circle.geometry.rotateX(THREE.MathUtils.degToRad(90));
		this.slamTracker.add(circle);

		// model tracker
		this.modelTracker = new THREE.Object3D();
		this.modelTracker.userData.requiresOutline = true;
	}

	onDispose = () =>
	{
		document.removeEventListener(CustomEventTypes.onImageTargetTextureLoaded, this.onDefaultTargetImageloaded);
	}

	onDefaultTargetImageloaded = () =>
	{
		if (!this.imageTexture) this.setImage(ARTracker.defaultImageTexture);
	}

	setVisible = (value: boolean) =>
	{
		this.visible = value;
	}

	getDisplayMode = () : RenderModes =>
	{
		return this.displayMode;
	}

	setDisplayMode = (mode: RenderModes) =>
	{
		this.displayMode = mode;
	}

	getTrackerType = () : TrackerTypes => 
	{
		return this.trackerType;
	}

	setTrackerType = (type: TrackerTypes) : void =>
	{
		this.trackerType = type;
		switch (this.trackerType)
		{
			case TrackerTypes.SLAM:
				this.imageTracker!.visible = false;
				this.slamTracker!.visible = true;
				this.modelTracker!.visible = false;
				this.geometry.copy(this.slamTracker!.geometry);

				let defaultTrackerExists = false;
				for (const tracker of ARTracker.getTrackerList())
				{
					if (tracker !== this && tracker.isDefaultTracker)
					{
						defaultTrackerExists = true;
						break;
					}
				}
				this.isDefaultTracker = !defaultTrackerExists;
				break;
			case TrackerTypes.IMAGE:
				this.imageTracker!.visible = true;
				this.slamTracker!.visible = false;
				this.modelTracker!.visible = false;

				this.geometry.copy(this.imageTracker!.geometry);
				break;
			case TrackerTypes.MODEL:
				this.imageTracker!.visible = false;
				this.slamTracker!.visible = false;
				this.modelTracker!.visible = true;
				if (this.modelInstance)
				{
					this.geometry.copy(this.modelInstance.geometry);
					this.geometry.scale(this.modelInstance.scale.x, this.modelInstance.scale.y, this.modelInstance.scale.z);
					this.geometry.applyQuaternion(this.modelInstance.quaternion);
					this.geometry.translate(this.modelInstance.position.x, this.modelInstance.position.y, this.modelInstance.position.z);
				}
				else
				{
					this.geometry = new THREE.BufferGeometry();
				}

				break;
			default:
				assert(false, "unsupported tracker type: " + this.trackerType);
		}
		const isSelected = Engine.getInstance().getPickingManager().isObjectSelected(this);
		if (isSelected) document.dispatchEvent(new CustomEvent(CustomEventTypes.objectSelectionChanged)); //cause the outline renderer to refresh
		ScenarioHealthUtils.CheckTrackerReferences();
	}

	getImage = () =>
	{
		return this.imageAssetId;
	}

	setImage = (value: THREE.Texture | string | null) =>
	{
		let texture: THREE.Texture | null;
		if (value)
		{
			if (value instanceof THREE.Texture)
			{
				texture = value;
			}
			else
			{
				texture = MaterialAsset.getTexture(value);
				if (!texture)
				{
					MaterialAsset.loadTexture(value).then((texture) =>
					{
						this.setImage(texture);
					});
					return;
				}
			}
		}
		else
		{
			texture = ARTracker.defaultImageTexture;
			this.imageAssetId = "";
		}

		if(!texture) return;
		
		this.imageAssetId = texture.userData?.assetId ? texture.userData.assetId : null;

		if (texture.colorSpace !== THREE.SRGBColorSpace)
		{
			texture.colorSpace = THREE.SRGBColorSpace;
			texture.needsUpdate = true;
		}

		this.imageTexture = texture;
		(this.imageTracker!.material as THREE.MeshBasicMaterial).map = this.imageTexture;
		(this.imageTracker!.material as THREE.MeshBasicMaterial).needsUpdate = true;
		this.imageAspectRatio = texture.image.width / texture.image.height;
		const isSelected = Engine.getInstance().getPickingManager().isObjectSelected(this);
		if (isSelected)
		{
			document.dispatchEvent(new CustomEvent(CustomEventTypes.objectSelectionChanged)); //cause the outline renderer to refresh
			document.dispatchEvent(new CustomEvent(CustomEventTypes.onTrackerListUpdated));
		}
		ScenarioHealthUtils.CheckTrackerReferences();
	}

	getImageSize = () =>
	{
		return this.imageSize;
	}

	setImageSize = (value: number) =>
	{
		this.imageSize = value;
		const isSelected = Engine.getInstance().getPickingManager().isObjectSelected(this);
		if (isSelected) document.dispatchEvent(new CustomEvent(CustomEventTypes.objectSelectionChanged)); //cause the outline renderer to refresh
	}

	getSceneSelectionModels = () =>
	{
		return this.sceneSelectionModels;
	}

	getModelAssetId = () =>
	{
		return this.modelAssetId;
	}

	onModelLoaded = () =>
	{
		AssetsManager.addUsedAsset(this.modelAssetId);
		const modelAsset = Engine.getInstance().getModelAsset(this.modelAssetId);
		this.modelInstance = SceneObjectUtils.cloneObject(modelAsset!) as THREE.Mesh;
		this.modelTracker!.add(this.modelInstance);
		this.finalizeModelObjects();
	}

	clearModelTrackingObjects = () =>
	{
		AssetsManager.removeAssetsUsedBySceneObject(this.modelTracker!);
		for (const child of this.modelTracker!.children)
		{
			if (child !== this.modelTrackerCamera)
			{
				SceneObjectUtils.disposeObject(child, false);
				this.modelTracker!.remove(child);
			}
		}
		if (this.modelPreviewInstance)
		{
			this.modelPreviewInstance.removeFromParent();
			SceneObjectUtils.disposeObject(this.modelPreviewInstance, false);
		}

		this.sceneSelectionModels.length = 0;

		this.modelAssetId = "";
	}

	setModelFromSceneSelection = (selectedObjects: THREE.Object3D[]) => 
	{
		this.clearModelTrackingObjects();

		const totalBounds = new THREE.Box3();
		const sharedParentFilteredObjects: THREE.Object3D[] = [];
		for (const object of selectedObjects)
		{
			let parentIsSelected = false;
			for (const parent of selectedObjects)
			{
				if (SceneObjectUtils.isDescendantOf(object, parent))
				{
					parentIsSelected = true;
					break;
				}
			}
			if (!parentIsSelected)
			{
				sharedParentFilteredObjects.push(object);
			}
		}

		for(const object of sharedParentFilteredObjects)
		{
			if (object instanceof THREE.Mesh)
			{
				SceneObjectUtils.expandBoundsByObjectVisibleOnly(totalBounds, object);
				this.sceneSelectionModels.push(object);
			}
			else
			{
				for(const child of object.children)
				{
					if(!child.userData.hideInHierarchy)	sharedParentFilteredObjects.push(child);
				}
			}		
		}

		if (!this.sceneSelectionModels.length)
		{
			this.modelAssetId = "";
			this.modelInstance = undefined;
			this.modelPreviewInstance = undefined;
			const isSelected = Engine.getInstance().getPickingManager().isObjectSelected(this);
			if (isSelected)
			{
				document.dispatchEvent(new CustomEvent(CustomEventTypes.objectSelectionChanged)); //cause the outline renderer to refresh
				document.dispatchEvent(new CustomEvent(CustomEventTypes.onTrackerListUpdated));
			}
			ScenarioHealthUtils.CheckTrackerReferences();
			return;
		}

		const boundsSize = new THREE.Vector3();
		totalBounds.getSize(boundsSize);
		if (this.sceneSelectionModels.length === 1)
		{
			this.updateWorldMatrix(true, false);
			this.modelInstance = new THREE.Mesh(new THREE.BoxGeometry(boundsSize.x, boundsSize.y, boundsSize.z), CustomMaterials.getModelRootMaterial());

			const object = this.sceneSelectionModels[0];
			const worldScale = new THREE.Vector3();
			object.matrixWorld.decompose(this.position, this.quaternion, worldScale);
			const boundsCenter = new THREE.Vector3();
			totalBounds.getCenter(boundsCenter);
			this.modelInstance.geometry.translate(boundsCenter.x, boundsCenter.y, boundsCenter.z);
			this.modelInstance.geometry.translate(-this.position.x, -this.position.y, -this.position.z);
			this.modelInstance.geometry.applyQuaternion(new THREE.Quaternion().copy(this.quaternion).invert());
		}
		else
		{
			totalBounds.getCenter(this.position);
			this.quaternion.set(0, 0, 0, 1);
			this.modelInstance = new THREE.Mesh(new THREE.BoxGeometry(boundsSize.x, boundsSize.y, boundsSize.z), CustomMaterials.getModelRootMaterial());
		}

		this.modelTracker!.add(this.modelInstance);

		for (const object of this.sceneSelectionModels)
		{
			const clone = SceneObjectUtils.cloneObject(object);
			object.updateWorldMatrix(true, false);
			object.matrixWorld.decompose(clone.position, clone.quaternion, clone.scale);
			this.modelInstance.attach(clone);
		}

		this.modelAssetId = getUuidv4();
		this.finalizeModelObjects();
		ScenarioHealthUtils.CheckTrackerReferences();
	}

	finalizeModelObjects = () =>
	{
		const depthLineMaterial = CustomMaterials.getDepthMaskLineMaterial();
		depthLineMaterial.lineColor = new THREE.Color().setRGB(0.5, 1.0, 1.0, THREE.SRGBColorSpace);
		const objectsToDiscard: THREE.Object3D[] = [];
		const incompleteSkeletons: THREE.Mesh[] = [];

		this.modelInstance!.traverse(function(child)
		{
			if ((!(child instanceof THREE.Mesh) && !(child instanceof THREE.Bone)) || child.userData.hideInHierarchy || child instanceof ScopeCamera || child instanceof ScopeLight ||
				child instanceof Text3D || child instanceof Callout ||child instanceof MediaPanel)
			{
				objectsToDiscard.push(child);
			}
			else
			{
				delete child.userData.markedSaveRoot;
				delete child.userData.saveModifications;
				delete child.userData.visibility;
				child.userData.requiresOutline = true;
				if (child instanceof THREE.Mesh)
				{
					const materialAsset = MaterialAsset.getMaterialAssetByObjectGuid(child.userData.guid);
					if (materialAsset)
					{
						MaterialAsset.disassociateMaterialAsset(child);
					}
					if (child.material.visible) child.material = depthLineMaterial;
				}

				if(child instanceof THREE.SkinnedMesh)
				{
					for(const bone of child.skeleton.bones)
					{
						if(!bone || bone.userData.hideInHierarchy)
						{
							incompleteSkeletons.push(child);
							break;
						}
					}
				}
			}
		});

		for (let i = objectsToDiscard.length - 1; i >= 0; i--)
		{
			const object = objectsToDiscard[i];
			const numChildren = object.children.length;
			for (let j = numChildren - 1; j >= 0; j--)
			{
				const child = object.children[j];
				if (!child.userData.hideInHierarchy)
				{
					object.parent!.attach(child);
				}

			}
			object.removeFromParent();
			SceneObjectUtils.disposeObject(object, false);
		}

		for(let i = incompleteSkeletons.length - 1; i >= 0; i--)
		{
			const skeletonToDiscard = incompleteSkeletons[i];
			const mesh = new THREE.Mesh();
			THREE.Mesh.prototype.copy.call(mesh, skeletonToDiscard, false);
			skeletonToDiscard.matrixWorld.decompose(mesh.position, mesh.quaternion, mesh.scale);
			skeletonToDiscard.parent!.attach(mesh);
			const numChildren = skeletonToDiscard.children.length;
			for (let j = numChildren - 1; j >= 0; j--)
			{
				const child = skeletonToDiscard.children[j];
				mesh.attach(child);
			}
			skeletonToDiscard.removeFromParent();
			SceneObjectUtils.disposeObject(skeletonToDiscard, false);
		}

		const previewInstance = SceneObjectUtils.cloneObject(this.modelInstance!) as THREE.Mesh;
		
		previewInstance.traverse(function(child)
		{
			if(child instanceof THREE.Mesh && (child.material as THREE.Material)?.visible) child.material = depthLineMaterial;
		})
		previewInstance.geometry.computeBoundingBox();
		this.modelPreviewInstance = new THREE.Mesh(previewInstance.geometry.clone(), CustomMaterials.getModelRootMaterial());
		this.modelPreviewInstance.name = "Object Tracker Preview";
		this.modelPreviewInstance.scale.copy(previewInstance.scale);
		this.modelPreviewInstance.userData.pivot = JSON.parse(JSON.stringify(defaultPivot));
		const boundsCenter = new THREE.Vector3();
		this.modelPreviewInstance!.geometry.boundingBox!.getCenter(boundsCenter);
		boundsCenter.multiply(this.modelPreviewInstance.scale);
		TransformUtils.applyPivot(this.modelPreviewInstance, boundsCenter);

		this.modelPreviewInstance.attach(previewInstance);
		this.posePreviewScene.add(this.modelPreviewInstance);

		this.geometry.copy(this.modelInstance!.geometry);
		this.geometry.scale(this.modelInstance!.scale.x, this.modelInstance!.scale.y, this.modelInstance!.scale.z);
		this.geometry.applyQuaternion(this.modelInstance!.quaternion);
		this.geometry.translate(this.modelInstance!.position.x, this.modelInstance!.position.y, this.modelInstance!.position.z);

		this.modelInstance!.traverse(function(child)
		{
			child.userData.hideInHierarchy = true;
			child.layers.disableAll();
			child.layers.enable(RenderLayers.MainCameraOnly);
		});
		
		this.resetPose();
		this.modelTrackerCamera.userData.renderIsDirty = true;
		this.modelTrackerCamera.userData.anchorDistance = this.modelTrackerCameraController.getDistanceToAnchor();

		ARTracker.createGlbBlob(this);
		const isSelected = Engine.getInstance().getPickingManager().isObjectSelected(this);
		if (isSelected)
		{
			document.dispatchEvent(new CustomEvent(CustomEventTypes.objectSelectionChanged)); //cause the outline renderer to refresh
			document.dispatchEvent(new CustomEvent(CustomEventTypes.onTrackerListUpdated));
		}

		AssetsManager.addAssetsUsedBySceneObject(this.modelTracker!);
	}

	resetPose = () =>
	{
		const previewInstance = this.modelPreviewInstance!.children[0] as THREE.Mesh;
		previewInstance.removeFromParent();
		this.modelPreviewInstance!.quaternion.set(0, 0, 0, 1);
		previewInstance.geometry.boundingBox!.getCenter(this.modelPreviewInstance!.position);
		this.modelPreviewInstance!.position.multiply(previewInstance.scale);
		this.modelPreviewInstance!.add(previewInstance);

		this.modelTrackerCamera.quaternion.set(0, 0, 0, 1);
		this.modelTrackerCamera.position.set(1, 1, 1);
		this.modelTrackerCamera.lookAt(0, 0, 0);

		if ( this.modelPreviewInstance )
		{
			this.modelTrackerCameraController.focus([this.modelPreviewInstance]);
		}
		this.modelTrackerCameraController.animateTransform = false;
		this.modelTrackerCamera.position.copy(this.modelTrackerCameraController.animationTargetPosition);
		this.modelTrackerCamera.lookAt(this.modelTrackerCameraController.getAnchor());
	}

	setModel = (value: string) => 
	{
		this.clearModelTrackingObjects();

		if (value)
		{
			this.modelAssetId = value;
			const modelAsset = Engine.getInstance().getModelAsset(value);
			if (!modelAsset)
			{
				StandbyOverlay.startStandbyMode();
				const url = AssetUtility.getProcessedAssetURL(value);
				const modelInfo = AssetsManager.getAssetInfo(value)!;
				const modelDownloadPromise: Promise<void>[] = [];
				modelDownloadPromise.push(Engine.getInstance().prepareModel(value, url, modelInfo.name, undefined, false, true));
				Promise.all(modelDownloadPromise).then(() => 
				{
					if (modelDownloadPromise.length) StandbyOverlay.stopStandbyMode();
					this.onModelLoaded();
				}).catch((error) => 
				{
					console.error(error);
					StandbyOverlay.stopStandbyMode();
				});
			}
			else
			{
				this.onModelLoaded();
			}
		}
		else
		{
			this.modelAssetId = "";
			this.modelInstance = undefined;
			this.modelPreviewInstance = undefined;
			const isSelected = Engine.getInstance().getPickingManager().isObjectSelected(this);
			if (isSelected)
			{
				document.dispatchEvent(new CustomEvent(CustomEventTypes.objectSelectionChanged)); //cause the outline renderer to refresh
				document.dispatchEvent(new CustomEvent(CustomEventTypes.onTrackerListUpdated));
			}
		}
		ScenarioHealthUtils.CheckTrackerReferences();
	}

	getExportBlob = () =>
	{
		return ARTracker.glbBlobs.get(this.modelAssetId);
	}

	getCameraExportTransform = (positionTarget: THREE.Vector3, rotationTarget: THREE.Quaternion) =>
	{
		//C2 camera is looking at modelPreviewInstance, while its exported transform should be looking at modelInstance
		//copy the local-to-previewInstance transform and apply the same relative values with regards to modelInstance, and then export the world-space values
		const initialCameraPos = new THREE.Vector3().copy(this.modelTrackerCamera.position);
		const initialCameraQuat = new THREE.Quaternion().copy(this.modelTrackerCamera.quaternion);
		const previewInstance = this.modelPreviewInstance!.children[0];
		previewInstance.attach(this.modelTrackerCamera);
		positionTarget.copy(this.modelTrackerCamera.position);
		rotationTarget.copy(this.modelTrackerCamera.quaternion);
		this.modelInstance!.attach(this.modelTrackerCamera);
		this.modelTrackerCamera.position.copy(positionTarget);
		this.modelTrackerCamera.quaternion.copy(rotationTarget);
		this.posePreviewScene.attach(this.modelTrackerCamera);

		positionTarget.copy(this.modelTrackerCamera.position);
		rotationTarget.copy(this.modelTrackerCamera.quaternion);

		this.modelTrackerCamera.position.copy(initialCameraPos);
		this.modelTrackerCamera.quaternion.copy(initialCameraQuat);
	}

	getIsDefaultTracker = () =>
	{
		return this.isDefaultTracker;
	}

	setIsDefaultTracker = (value: boolean)	=>
	{
		if (value)
		{
			for (const tracker of ARTracker.getTrackerList())
			{
				tracker.isDefaultTracker = false;
			}
		}
		this.isDefaultTracker = value;
	}

	getPoseAutoInit = () => 
	{
		return this.autoInit;
	}

	setPoseAutoInit = (value: boolean) => 
	{
		this.autoInit = value;
		if (this.modelPreviewInstance)
		{
			this.modelTrackerCamera.userData.renderIsDirty = true;
		}
	}

	getLaplaceThreshold = () =>
	{
		return this.laplaceThreshold;
	}

	setLaplaceThreshold = (value: number) =>
	{
		this.laplaceThreshold = value;
	}

	getNormalThreshold = () => 
	{
		return this.normalThreshold;
	}

	setNormalThreshold = (value: number) =>
	{
		this.normalThreshold = value;
	}

	getMinInlierRatioInit = () =>
	{
		return this.minInlierRatioInit;
	}

	setMinInlierRatioInit = (value: number) =>
	{
		this.minInlierRatioInit = value;
	}

	getMinInlierRatioTracking = () =>
	{
		return this.minInlierRatioTracking;
	}

	setMinInlierRatioTracking = (value: number) =>
	{
		this.minInlierRatioTracking = value;
	}

	getLineGradientThreshold = () =>
	{
		return this.lineGradientThreshold;
	}

	setLineGradientThreshold = (value: number) =>
	{
		this.lineGradientThreshold = value;
	}

	getLineSearchLengthInitRelative = () =>
	{
		return this.lineSearchLengthInitRelative;
	}

	setLineSearchLengthInitRelative = (value: number) =>
	{
		this.lineSearchLengthInitRelative = value;
	}

	getLineSearchLengthTrackingRelative = () =>
	{
		return this.lineSearchLengthTrackingRelative;
	}

	setLineSearchLengthTrackingRelative = (value: number) =>
	{
		this.lineSearchLengthTrackingRelative = value;
	}

	getKeyFrameDistance = () =>
	{
		return this.keyFrameDistance;
	}

	setKeyFrameDistance = (value: number) =>
	{
		this.keyFrameDistance = value;
	}

	getFovPercent = () => 
	{
		return this.fovPercent;
	}

	setFovPercent = (value: number) =>
	{
		this.fovPercent = value;
	}

	getGuideImageAssetId = () => 
	{
		return this.guideImageAssetId;
	}

	setGuideImageAssetId = (value: string) =>
	{
		this.guideImageAssetId = value;
	}

	getGuideTitle = () =>
	{
		return this.guideTitle;
	}

	setGuideTitle = (value: string) =>
	{
		this.guideTitle = value;
	}
	
	getGuideTitleVisible = () =>
	{
		return this.guideTitleVisible;
	}
	
	setGuideTitleVisible = (value: boolean) =>
	{
		this.guideTitleVisible = value;
	}

	getGuideCaption = () =>
	{
		return this.guideCaption;
	}
	
	setGuideCaption = (value: string) =>
	{
		this.guideCaption = value;
	}
	
	getGuideCaptionVisible = () =>
	{
		return this.guideCaptionVisible;
	}

	setGuideCaptionVisible = (value: boolean) =>
	{
		this.guideCaptionVisible = value;
	}

	getGuideSize = () =>
	{
		return this.guideSize;
	}

	setGuideSize = (value: number) =>
	{
		this.guideSize = value;
	}

	getContentCanBeScaled = () =>
	{
		return this.contentCanBeScaled;
	}

	setContentCanBeScaled = (value: boolean) =>
	{
		this.contentCanBeScaled = value;
	}

	getContentCanBeRotated = () =>
	{
		return this.contentCanBeRotated;
	}

	setContentCanBeRotated = (value: boolean) =>
	{
		this.contentCanBeRotated = value;
	}

	onSelected = () =>
	{
		if (!this.autoInit && this.modelPreviewInstance)
		{
			this.modelTrackerCamera.userData.renderIsDirty = true;
		}
	}

	onDeselected = () =>
	{
	}

	copy = (source: ARTracker, recursive = true) =>
	{
		THREE.Object3D.prototype.copy.call( this, source, false );

		this.sequence = source.sequence;
		this.imageAssetId = source.imageAssetId;
		this.setImage(source.imageTexture ?? null);
		this.setImageSize(source.imageSize);
		this.setDisplayMode(source.displayMode);
		this.setPoseAutoInit(source.autoInit);
		if (source.sceneSelectionModels.length)
		{
			this.setModelFromSceneSelection(source.sceneSelectionModels);
		}
		else
		{
			this.setModel(source.modelAssetId);
		}

		this.setLaplaceThreshold(source.laplaceThreshold);
		this.setNormalThreshold(source.normalThreshold);
		this.setMinInlierRatioInit(source.minInlierRatioInit);
		this.setMinInlierRatioTracking(source.minInlierRatioTracking);
		this.setLineGradientThreshold(source.lineGradientThreshold);
		this.setLineSearchLengthInitRelative(source.lineSearchLengthInitRelative);
		this.setLineSearchLengthTrackingRelative(source.lineSearchLengthTrackingRelative);
		this.setKeyFrameDistance(source.keyFrameDistance);
		this.setGuideImageAssetId(source.guideImageAssetId);
		this.setGuideTitle(source.guideTitle);
		this.setGuideCaption(source.guideCaption);
		this.setGuideSize(source.guideSize);
		this.modelTrackerCamera.position.copy(source.modelTrackerCamera.position);
		this.modelTrackerCamera.rotation.copy(source.modelTrackerCamera.rotation);
		if (source.modelInstance)
		{
			this.modelPreviewInstance!.position.copy(source.modelPreviewInstance!.position);
			this.modelPreviewInstance!.rotation.copy(source.modelPreviewInstance!.rotation);
		}

		this.setTrackerType(source.trackerType);
		return this;
	}

	updateMatrixWorld = (force: boolean) =>
	{
		switch (this.trackerType)
		{
			case TrackerTypes.IMAGE:
				this.imageTracker!.position.y = 0.001;
				if (this.imageAspectRatio >= 1.0)	this.scale.set(this.imageSize, 1.0, this.imageSize / this.imageAspectRatio);
				else								this.scale.set(this.imageSize * this.imageAspectRatio, 1.0, this.imageSize);
				break;
			case TrackerTypes.SLAM:
				this.scale.set(1, 1, 1).multiplyScalar(0.5);
				break;
			case TrackerTypes.MODEL:
				this.scale.set(1, 1, 1);
				break;
		}

		if (this.matrixAutoUpdate)
		{this.updateMatrix();}

		if (this.matrixWorldNeedsUpdate || force)
		{
			if (this.parent === null)
			{
				this.matrixWorld.copy(this.matrix);
			}
			else
			{
				this.matrixWorld.multiplyMatrices(this.parent.matrixWorld, this.matrix);
			}

			this.matrixWorldNeedsUpdate = false;
			force = true;
		}

		// update children
		const children = this.children;
		for (let i = 0, l = children.length; i < l; i++)
		{
			children[i].updateMatrixWorld(force);
		}
	}
}

export default ARTracker;
